import React, { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { CustomPart } from "../../../stores/slices/packageEditorSlice";
import { Input } from "../../../ui/Input/Input";
import { useForm } from "../../../util/useForm";

export type CustomPartForm = {
  customPrice: number;
};

const schema = z.object({
  customName: z.string().nonempty({ message: "form.error.customName" }),
  customNameFr: z.string().default(""),
  customPartNumber: z
    .string()
    .nonempty({ message: "form.error.customPartNumber" }),
  customPrice: z
    .number({ coerce: true })
    .optional()
    .refine(
      (value) => {
        if (!value) return true;
        return value > 0;
      },
      {
        message: "form.error.customPrice",
      }
    )
    .refine((value) => /^\d+(\.\d{1,2})?$/.test(value?.toString() || ""), {
      message: "form.error.pricePrecision",
    }),
  customTypeName: z.string().nonempty({ message: "form.error.customTypeName" }),
  customTypeNameFr: z.string().default(""),
  quantity: z
    .number({ coerce: true })
    .int()
    .min(1, {
      message: "form.error.packageQuantity",
    })
    .max(16, {
      message: "form.error.packageQuantity",
    }),
});

export const customPartFormId = "new-custom-part-form";

export const CustomPartForm: FunctionComponent<{
  handleReset: VoidFunction;
  handleSubmit(form: CustomPart): void;
}> = ({ handleReset, handleSubmit }) => {
  const { t } = useTranslation();
  const [handleForm] = useForm({
    onSubmit({
      customName,
      customNameFr,
      customPartNumber,
      customPrice,
      customTypeName,
      customTypeNameFr,
      quantity,
    }) {
      handleSubmit({
        customName,
        customNameFr,
        customPartNumber,
        customPrice: String(customPrice),
        customTypeName,
        customTypeNameFr,
        quantity,
      });
    },
    schema,
  });
  return (
    <form
      className="p-4 space-y-6"
      id={customPartFormId}
      noValidate
      onReset={handleReset}
      onSubmit={handleForm}
    >
      <fieldset className="grid gap-2">
        <label
          className="text-xs text-ink-2 font-thin"
          htmlFor="custom-part-name"
        >
          {t("packageEditor.customName")}
        </label>
        <Input
          aria-label="customPartName"
          id="custom-part-name"
          name="customName"
          type="text"
        />
      </fieldset>
      <fieldset className="grid gap-2">
        <label
          className="text-xs text-ink-2 font-thin"
          htmlFor="custom-part-name-fr"
        >
          {t("packageEditor.customNameFr")}
        </label>
        <Input
          aria-label="customPartNameFr"
          id="custom-part-name-fr"
          name="customNameFr"
          type="text"
        />
      </fieldset>
      <fieldset className="grid gap-2">
        <label
          className="text-xs text-ink-2 font-thin"
          htmlFor="custom-type-name"
        >
          {t("packageEditor.customTypeName")}
        </label>
        <Input
          aria-label="customTypeName"
          id="custom-type-name"
          name="customTypeName"
          type="text"
        />
      </fieldset>
      <fieldset className="grid gap-2">
        <label
          className="text-xs text-ink-2 font-thin"
          htmlFor="custom-type-name-fr"
        >
          {t("packageEditor.customTypeNameFr")}
        </label>
        <Input
          aria-label="customTypeNameFr"
          id="custom-type-name-fr"
          name="customTypeNameFr"
          type="text"
        />
      </fieldset>
      <fieldset className="grid gap-2">
        <label
          className="text-xs text-ink-2 font-thin"
          htmlFor="custom-part-number"
        >
          {t("packageEditor.partNumber")}
        </label>
        <Input
          aria-label="customPartNumber"
          id="custom-part-number"
          name="customPartNumber"
          type="text"
        />
      </fieldset>
      <fieldset className="grid gap-2">
        <label
          className="text-xs text-ink-2 font-thin"
          htmlFor="custom-part-price"
        >
          {t("packageEditor.customPrice")}
        </label>
        <Input
          aria-label="customPartPrice"
          id="custom-part-price"
          inputMode="decimal"
          name="customPrice"
          type="text"
        />
      </fieldset>
      <fieldset className="grid gap-2">
        <label
          className="text-xs text-ink-2 font-thin"
          htmlFor="custom-part-quantity"
        >
          {t("packageEditor.quantity")}
        </label>
        <Input
          aria-label="quantity"
          id="custom-part-quantity"
          inputMode="decimal"
          name="quantity"
          type="text"
        />
      </fieldset>
    </form>
  );
};
